import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet, apiPost } from '../api';

const HOSTS_PER_PAGE = 20;

const avatarColors = ['#ef4444','#f97316','#eab308','#22c55e','#06b6d4','#3b82f6','#8b5cf6','#ec4899'];
const getAvatarColor = (name = '') => avatarColors[name.charCodeAt(0) % avatarColors.length];
const getInitials = (name = '') => name.slice(0, 2).toUpperCase();

const AllHost = () => {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  // The backend scopes hosts to the caller's own agency (req.uid), so no
  // separate agency-id lookup is needed.
  // The paging offset is read from a ref, not from `hosts` itself: depending on
  // hosts.length would rebuild fetchHosts after every page and re-trigger the
  // mount effect that calls it. With the ref, fetchHosts is stable and the
  // effect can list it honestly instead of silencing the lint rule.
  const hostsRef = useRef([]);
  useEffect(() => { hostsRef.current = hosts; }, [hosts]);

  const fetchHosts = useCallback(async (loadMore = false) => {
    if (loadMore) setLoadingMore(true); else setLoading(true);
    try {
      const skip = loadMore ? hostsRef.current.length : 0;
      const data = await apiGet(`/api/account/hosts?skip=${skip}&limit=${HOSTS_PER_PAGE}`);
      const mapped = (data.hosts || []).map(h => ({
        ...h,
        joinDate: h.hostSince ? new Date(h.hostSince).toLocaleDateString('en-GB') : 'N/A',
      }));
      setHosts(prev => loadMore ? [...prev, ...mapped] : mapped);
      setHasMore(!!data.hasMore);
      setError(null);
    } catch { setError(loadMore ? 'Error loading more hosts.' : 'Error fetching hosts.'); }
    finally { setLoading(false); setLoadingMore(false); }
  }, []);

  useEffect(() => { fetchHosts(false); }, [fetchHosts]);

  const handleLoadMore = () => { if (hasMore) fetchHosts(true); };

  const handleRemoveHost = async (hostId, hostName) => {
    if (!window.confirm(`Remove ${hostName} from your agency? Their role will revert to 'user'.`)) return;
    setRemovingId(hostId);
    try {
      const res = await apiPost('/api/account/hosts/remove', { hostId });
      if (res.success === false) throw new Error(res.message);
      setHosts(prev => prev.filter(h => h.id !== hostId));
    } catch { alert('Failed to remove host. Please try again.'); }
    finally { setRemovingId(null); }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <div className="w-10 h-10 rounded-full border-4 border-white/10 border-l-[#ff4d6d] animate-spin" />
      <p className="text-sm text-white/40">Loading Hosts...</p>
    </div>
  );

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-white">All Hosts</h1>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Hosts registered under your agency</p>
        </div>
        {hosts.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold" style={{ background: 'rgba(59,130,246,0.12)', color: '#93c5fd', border: '1px solid rgba(59,130,246,0.25)' }}>
            <i className="fas fa-users text-xs" />
            {hosts.length}{hasMore ? '+' : ''} hosts
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-xl p-3.5 flex items-center gap-2 text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171' }}>
          <i className="fas fa-exclamation-circle" />
          {error}
        </div>
      )}

      {hosts.length === 0 && !error ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 rounded-2xl" style={{ border: '2px dashed rgba(255,255,255,0.08)' }}>
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <i className="fas fa-users text-2xl" style={{ color: 'rgba(255,255,255,0.2)' }} />
          </div>
          <p className="font-semibold text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>No hosts assigned to your agency yet</p>
        </div>
      ) : (
        <>
          {/* Host list */}
          <div className="space-y-2">
            {hosts.map((host, index) => (
              <div
                key={host.id}
                className="flex items-center gap-3 rounded-xl px-4 py-3 transition-all"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
                onMouseEnter={e => e.currentTarget.style.border = '1px solid rgba(255,255,255,0.12)'}
                onMouseLeave={e => e.currentTarget.style.border = '1px solid rgba(255,255,255,0.07)'}
              >
                {/* Rank */}
                <span className="font-bold text-xs w-6 text-right flex-shrink-0" style={{ color: 'rgba(255,255,255,0.25)' }}>
                  {index + 1}
                </span>

                {/* Avatar */}
                <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm" style={{ background: getAvatarColor(host.username) }}>
                  {getInitials(host.username)}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white text-sm truncate">{host.username}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{host.ID}</span>
                    {host.hostType && (
                      <span className="text-xs font-semibold rounded-full px-2 py-0.5" style={{
                        background: host.hostType === 'video' ? 'rgba(139,92,246,0.15)' : 'rgba(56,189,248,0.15)',
                        color: host.hostType === 'video' ? '#c4b5fd' : '#7dd3fc',
                        border: `1px solid ${host.hostType === 'video' ? 'rgba(139,92,246,0.3)' : 'rgba(56,189,248,0.3)'}`,
                      }}>
                        <i className={`fas ${host.hostType === 'video' ? 'fa-video' : 'fa-microphone'} mr-1 text-xs`} />
                        {host.hostType}
                      </span>
                    )}
                  </div>
                </div>

                {/* Join date */}
                <div className="hidden sm:block text-right flex-shrink-0">
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>Joined</p>
                  <p className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.55)' }}>{host.joinDate}</p>
                </div>

                {/* Remove */}
                <button
                  onClick={() => handleRemoveHost(host.id, host.username)}
                  disabled={removingId === host.id}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                  style={{ background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.25)', opacity: removingId === host.id ? 0.5 : 1, cursor: removingId === host.id ? 'not-allowed' : 'pointer' }}
                >
                  {removingId === host.id
                    ? <><i className="fas fa-spinner fa-spin" />Removing</>
                    : <><i className="fas fa-user-times" />Remove</>
                  }
                </button>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="text-center pt-2">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all"
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)', opacity: loadingMore ? 0.5 : 1, cursor: loadingMore ? 'not-allowed' : 'pointer' }}
              >
                {loadingMore ? <><i className="fas fa-spinner fa-spin" />Loading...</> : <><i className="fas fa-chevron-down" />Load More Hosts</>}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AllHost;
